import { useCallback, useMemo } from 'react';
import { push } from 'redux-first-router';
import { useUser } from '@/hooks/useUser';
import { ORDERED_MARKETS, PRIMARY_MARKETS } from '@/constants/markets';
import { getStocksPath } from '../../utils';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import type { SortBy } from '../../hooks/useSortByOptions';
import { DEFAULT_MARKET } from '@/pages/Stocks/constants';

const IGNORED_MARKETS = [
  'lb', // Lebanon is ignored
];

export const useMarketFilter = ({
  industry,
  sortBy,
}: {
  industry?: string;
  sortBy?: SortBy;
}) => {
  const { data: user } = useUser();
  const isLoggedIn = user?.loggedIn;

  const marketOptions = useMemo(
    () => [
      {
        option: DEFAULT_MARKET,
        label: 'Global',
        href: getStocksPath({
          market: DEFAULT_MARKET,
          industry,
          sortBy,
          isLoggedIn,
        }),
      },
      ...ORDERED_MARKETS.filter((m) => !IGNORED_MARKETS.includes(m)).map(
        (market) => {
          return {
            option: market,
            label: PRIMARY_MARKETS[market].label,
            href: getStocksPath({ market, industry, sortBy, isLoggedIn }),
          };
        }
      ),
    ],
    [industry, sortBy, isLoggedIn]
  );

  const handleMarketChange = useCallback(
    (market: PrimaryCountryISOElseGlobal) => {
      const action = getStocksPath({ market, industry, sortBy, isLoggedIn });
      push(action);
    },
    [industry, sortBy, isLoggedIn]
  );

  return { marketOptions, handleMarketChange };
};
